import type {
  NewMenuComboOrderItemInput,
  NewMenuFormulaOrderItemInput,
  NewOrderItemInput,
  Order,
  UpdateOrderRequest,
} from "@/redux/services/order/order.type";
import { notesFromOrder } from "./cartFromOrder";
import type { CartEntry, DraftOrder } from "./types";

export interface OrderItemsPayload {
  orderItems: NewOrderItemInput[];
  menuComboOrderItems?: NewMenuComboOrderItemInput[];
  menuFormulaOrderItems?: NewMenuFormulaOrderItemInput[];
}

/**
 * On a committed order full edits are blocked; only add-ons are allowed and
 * existing lines must be echoed back as IRIs (a dropped line is a `422`).
 */
export type OrderEditMode = "full" | "add_only";

export function editModeForOrder(order: Order): OrderEditMode {
  return order.canBeModified ? "full" : "add_only";
}

// ----- Per-entry → new-line object builders (quantity can be overridden so we
// can emit just the *added* delta on top of a kept existing line). -----

function regularItemObject(
  entry: CartEntry,
  quantity: number,
): NewOrderItemInput {
  return {
    menuItem:
      entry.type === "item" && entry.menuItem?.id
        ? `/api/menu_items/${entry.menuItem.id}`
        : undefined,
    dailyDish:
      entry.type === "daily_dish" && entry.dailyDishId
        ? `/api/daily_dishes/${entry.dailyDishId}`
        : undefined,
    quantity,
    additionalDemands: entry.additionalDemands || undefined,
    selectedMenuItemOptionValues:
      entry.selectedOptionValueIds.length > 0
        ? entry.selectedOptionValueIds.map(
            (id) => `/api/menu_item_option_values/${id}`,
          )
        : undefined,
  };
}

function comboItemObject(
  entry: CartEntry,
  quantity: number,
): NewMenuComboOrderItemInput {
  return {
    menuCombo: `/api/menu_combos/${entry.menuComboId}`,
    quantity,
    additionalDemands: entry.additionalDemands || undefined,
    selections:
      entry.comboSelections && entry.comboSelections.length > 0
        ? entry.comboSelections.map((s) => ({
            menuItem: `/api/menu_items/${s.menuItem}`,
            sideType: s.sideType,
            // The API requires each dish's required options on its selection
            // (MenuComboOrderItemConstraintValidator, SFRESTO-183).
            ...(s.selectedOptions && s.selectedOptions.length > 0
              ? {
                  selectedOptions: s.selectedOptions.map(
                    (id) => `/api/menu_item_option_values/${id}`,
                  ),
                }
              : {}),
          }))
        : undefined,
  };
}

function formulaItemObject(
  entry: CartEntry,
  quantity: number,
): NewMenuFormulaOrderItemInput {
  return {
    menuFormula: `/api/menu_formulas/${entry.menuFormulaId}`,
    quantity,
    additionalDemands: entry.additionalDemands || undefined,
    selections: (entry.formulaSelections ?? []).map((s) => ({
      menuFormulaComponentItem: `/api/menu_formula_component_items/${s.menuFormulaComponentItemId}`,
      ...(s.menuItemId
        ? { menuItem: `/api/menu_items/${s.menuItemId}` }
        : {}),
      ...(s.selectedOptions && s.selectedOptions.length > 0
        ? {
            selectedOptions: s.selectedOptions.map(
              (id) => `/api/menu_item_option_values/${id}`,
            ),
          }
        : {}),
    })),
  };
}

function splitCart(cart: CartEntry[]) {
  return {
    regularItems: cart.filter(
      (entry) => entry.type !== "combo" && entry.type !== "formula",
    ),
    comboItems: cart.filter((entry) => entry.type === "combo"),
    formulaItems: cart.filter((entry) => entry.type === "formula"),
  };
}

/**
 * Build the IRI-shaped item collections for POST /api/orders (create flow).
 * Every entry is a brand-new line, so they're all emitted as objects.
 */
export function buildOrderItemsPayload(cart: CartEntry[]): OrderItemsPayload {
  const { regularItems, comboItems, formulaItems } = splitCart(cart);

  return {
    orderItems: regularItems.map((entry) =>
      regularItemObject(entry, entry.quantity),
    ),
    menuComboOrderItems:
      comboItems.length > 0
        ? comboItems.map((entry) => comboItemObject(entry, entry.quantity))
        : undefined,
    menuFormulaOrderItems:
      formulaItems.length > 0
        ? formulaItems.map((entry) => formulaItemObject(entry, entry.quantity))
        : undefined,
  };
}

/**
 * Diff a hydrated collection against its original lines for a PATCH body.
 *
 * Each element is either the line's IRI string ("keep as-is") or a new-line
 * object. Unchanged existing lines are echoed as IRIs so their id + kitchen
 * status survive; quantity increases keep the IRI and add a separate delta
 * line (the prepared portion is preserved); brand-new entries are objects.
 *
 * - `full`: a removed line is simply omitted (= removal); a decreased line is
 *   recreated at its new quantity.
 * - `add_only`: every original line is always re-sent as an IRI (removals and
 *   decreases are forbidden — `422`); only additions are appended.
 */
function buildEditCollection<T>(
  entries: CartEntry[],
  originalIris: string[],
  editMode: OrderEditMode,
  toObject: (entry: CartEntry, quantity: number) => T,
): Array<string | T> {
  if (editMode === "add_only") {
    const out: Array<string | T> = [...originalIris];
    for (const entry of entries) {
      if (!entry.existingIri) {
        out.push(toObject(entry, entry.quantity));
      } else {
        const original = entry.originalQuantity ?? entry.quantity;
        if (entry.quantity > original) {
          out.push(toObject(entry, entry.quantity - original));
        }
      }
    }
    return out;
  }

  const out: Array<string | T> = [];
  for (const entry of entries) {
    if (!entry.existingIri) {
      out.push(toObject(entry, entry.quantity));
      continue;
    }
    const original = entry.originalQuantity ?? entry.quantity;
    if (entry.quantity === original) {
      out.push(entry.existingIri);
    } else if (entry.quantity > original) {
      out.push(entry.existingIri);
      out.push(toObject(entry, entry.quantity - original));
    } else {
      // Decreased: recreate at the new quantity (remove + add).
      out.push(toObject(entry, entry.quantity));
    }
  }
  return out;
}

/**
 * Build the merge-patch body for PATCH /api/orders/{id}. Item collections are
 * diffed against the original order (see buildEditCollection); order-level
 * fields (notes, table, delivery) are only sent when they differ. Customer
 * fields are intentionally omitted — that flow runs through a separate UI.
 */
export function buildUpdatePayload({
  draft,
  originalOrder,
}: {
  draft: DraftOrder;
  originalOrder: Order;
}): UpdateOrderRequest {
  const editMode = editModeForOrder(originalOrder);
  const { regularItems, comboItems, formulaItems } = splitCart(draft.cart);

  const payload: UpdateOrderRequest = {
    orderItems: buildEditCollection(
      regularItems,
      (originalOrder.orderItems ?? [])
        .map((l) => l["@id"])
        .filter((iri): iri is string => !!iri),
      editMode,
      regularItemObject,
    ),
    menuComboOrderItems: buildEditCollection(
      comboItems,
      (originalOrder.menuComboOrderItems ?? [])
        .map((l) => l["@id"])
        .filter((iri): iri is string => !!iri),
      editMode,
      comboItemObject,
    ),
    menuFormulaOrderItems: buildEditCollection(
      formulaItems,
      (originalOrder.menuFormulaOrderItems ?? [])
        .map((l) => l["@id"])
        .filter((iri): iri is string => !!iri),
      editMode,
      formulaItemObject,
    ),
  };

  const trimmedNotes = draft.notes?.trim() ?? "";
  const originalNotes = notesFromOrder(originalOrder);
  if (trimmedNotes !== originalNotes) {
    payload.notes = trimmedNotes;
  }

  // Optional edit reason — recorded to the order's modification history when
  // provided; omitted entirely when blank so it stays truly optional.
  const trimmedReason = draft.modificationReason?.trim() ?? "";
  if (trimmedReason !== "") {
    payload.modificationReason = trimmedReason;
  }

  if (
    draft.orderType === "dine_in" &&
    draft.tableId &&
    draft.tableId !== originalOrder.restaurantTable?.id
  ) {
    payload.restaurantTable = `/api/restaurant_tables/${draft.tableId}`;
  }

  if (draft.orderType === "delivery") {
    const trimmedAddress = draft.deliveryAddress?.trim() ?? "";
    const originalAddress = originalOrder.deliveryAddress ?? "";
    if (trimmedAddress && trimmedAddress !== originalAddress) {
      payload.deliveryAddress = trimmedAddress;
    }

    const newFee = draft.deliveryFee;
    const originalFee = originalOrder.deliveryFee;
    if (
      typeof newFee === "number" &&
      Number.isFinite(newFee) &&
      newFee !== originalFee
    ) {
      payload.deliveryFee = newFee;
    }

    const meta = (originalOrder.metadata ?? {}) as Record<string, unknown>;
    const trimmedPersonName = draft.deliveryPersonName?.trim() ?? "";
    const trimmedPersonPhone = draft.deliveryPersonPhone?.trim() ?? "";
    const originalPersonName =
      typeof meta.deliveryPersonName === "string"
        ? meta.deliveryPersonName
        : "";
    const originalPersonPhone =
      typeof meta.deliveryPersonPhone === "string"
        ? meta.deliveryPersonPhone
        : "";
    const metadataPatch: Record<string, unknown> = {};
    if (trimmedPersonName !== originalPersonName) {
      metadataPatch.deliveryPersonName = trimmedPersonName || null;
    }
    if (trimmedPersonPhone !== originalPersonPhone) {
      metadataPatch.deliveryPersonPhone = trimmedPersonPhone || null;
    }
    if (Object.keys(metadataPatch).length > 0) {
      payload.metadata = metadataPatch;
    }
  }

  if (draft.orderType === "dine_in") {
    const meta = (originalOrder.metadata ?? {}) as Record<string, unknown>;
    const originalGuestCount =
      typeof meta.guestCount === "number" ? meta.guestCount : undefined;
    const newGuestCount =
      typeof draft.guestCount === "number" && draft.guestCount > 0
        ? draft.guestCount
        : undefined;
    if (newGuestCount !== originalGuestCount) {
      // Merge-patch: a number to set it, null to clear a previously-set count.
      payload.metadata = {
        ...(payload.metadata ?? {}),
        guestCount: newGuestCount ?? null,
      };
    }
  }

  return payload;
}
